import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CPPUtils from 'services/CPPUtils';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import SaveButtonView from 'components/Common/SaveButtonView';
import {
  DrugbankInputs,
  DrugInputs,
} from 'components/HealthRecords/Prescriptions/NewPrescription/view';
import { cleanDrugbankProduct } from 'components/HealthRecords/Prescriptions/NewPrescription';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { useReduxForm, setFormValues } from 'hooks/useReduxForm';

const medicationSchema = z.object({
  name: z.string().optional().or(z.literal('')),
  dosage: z.string().optional().or(z.literal('')),
  dosage_unit: z.string().optional().or(z.literal('')),
  frequency: z.string().optional().or(z.literal('')),
  quantity: z.string().optional().or(z.literal('')),
  quantity_unit: z.string().optional().or(z.literal('')),
  time: z.string().optional().or(z.literal('')),
  time_unit: z.string().optional().or(z.literal('')),
  refills: z.string().optional().or(z.literal('')),
  route: z.string().optional().or(z.literal('')),
  comments: z.string().optional().or(z.literal('')),
  medications: z.any().optional(),
  additional_information: z.string().optional().or(z.literal('')),
  dosing_instructions: z.any().optional(),
  waiting_approval: z.any().optional(),
  pharmacy: z.any().optional(),
  disable_refills: z.any().optional(),
  prn: z.any().optional(),
  originalPharmacy: z.any().optional(),
  note: z.string().optional().or(z.literal('')),
  refill: z.any().optional(),
  attachments: z.any().optional(),
  drugbank_product: z.any().optional(),
});
type MedicationValues = z.infer<typeof medicationSchema>;

const defaultValues: MedicationValues = {
  name: '',
  dosage: '',
  dosage_unit: '',
  frequency: '',
  quantity: '',
  quantity_unit: '',
  time: '',
  time_unit: '',
  refills: '',
  route: '',
  comments: '',
  medications: undefined,
  additional_information: '',
  dosing_instructions: undefined,
  waiting_approval: undefined,
  pharmacy: undefined,
  disable_refills: undefined,
  prn: undefined,
  originalPharmacy: undefined,
  note: '',
  refill: undefined,
  attachments: undefined,
  drugbank_product: undefined,
};

interface CPPPatientMedicationInputProps {
  title: string;
  field: string;
  patient: any;
  onClose?: () => void;
  onSave?: (data: any) => void;
  saveOnSelectProduct?: boolean;
  initialValue?: Partial<MedicationValues>;
}

const CPPPatientMedicationInput = ({
  title,
  field,
  patient,
  onClose,
  onSave,
  saveOnSelectProduct = false,
  initialValue,
}: CPPPatientMedicationInputProps) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [drugBankKey, setDrugbankKey] = useState(0);

  const formName = CPPUtils.getFormName(field, patient.id);

  const { control, watch, setValue, reset } = useReduxForm<MedicationValues>(formName, {
    destroyOnUnmount: false,
    resolver: zodResolver(medicationSchema),
    defaultValues,
  });

  const formValues = watch();

  useEffect(() => {
    if (initialValue) {
      dispatch(setFormValues({ formName, values: initialValue as Record<string, unknown> }));
    }
  }, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onClickedSave(
    event?: React.MouseEvent,
    overrideData: Partial<MedicationValues> = {},
  ) {
    event?.stopPropagation();

    let medication: any = {
      prn: formValues.prn,
      comments: formValues.comments,
      dosage: formValues.dosage,
      dosage_unit: formValues.dosage_unit,
      frequency: formValues.frequency,
      name: formValues.name,
      quantity: formValues.quantity,
      quantity_unit: formValues.quantity_unit,
      dosing_instructions: formValues.dosing_instructions,
      refills: formValues.refills,
      route: formValues.route,
      time: formValues.time,
      time_unit: formValues.time_unit,
      drugbank_product: formValues.drugbank_product,
      ...overrideData,
    };

    if (medication.drugbank_product) {
      medication.drugbank_product = cleanDrugbankProduct(medication.drugbank_product);
    }

    setLoading(true);
    let data = PerformanceUtils.clone({
      value: {
        status: undefined,
        name: medication.name,
        prescription_medication: medication,
        notes: CPPUtils.getPrescriptionMedicationSummary(medication),
      },
    });

    if (onSave) {
      clearForm();
      onSave(data);
    } else {
      let result = await dispatch(
        api.HealthRecords.addOrEditHealthRecordPatientMedication({
          patientId: patient.id,
          patientMedications: [data],
        }),
        {
          errorTitle: title,
        },
      );
      setLoading(false);
      if (result) clearForm();
    }
  }

  function clearForm() {
    reset();
    setDrugbankKey(drugBankKey + 1);
  }

  function selectProduct(name: string | null, product: any) {
    setValue('name', name || '');
    setValue('drugbank_product', product);
    if (saveOnSelectProduct) {
      onClickedSave(undefined, { name: name || '', drugbank_product: product });
    }
  }

  const buttonDisabled = false;

  return (
    <div className="cpp-input cpp-medical-issue-input">
      {onClose !== undefined && <TopBar title={title} onClose={onClose} onClear={clearForm} />}
      <div className="details-panel">
        <div className="row form full-width" key={drugBankKey}>
          <div className="clear-form" onClick={clearForm}>
            Clear
          </div>
          <DrugbankInputs selectProduct={selectProduct} change={setValue} />
          <DrugInputs {...formValues} change={setValue} control={control} />
        </div>
        <SaveButtonView
          title="Save Medication"
          onClick={onClickedSave as any}
          isLoading={loading}
          isDisabled={buttonDisabled}
        />
      </div>
    </div>
  );
};

export default CPPPatientMedicationInput;
